import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { sessionSelectors } from 'store/state/selectors';

const STORAGE_PREFIX = 'ecommerce:columns';

const buildStorageKey = (scope: string, userId: number) =>
  `${STORAGE_PREFIX}:${Number.isNaN(userId) ? 'anonymous' : userId}:${scope}`;

const readHiddenKeys = (storageKey: string): string[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return [];
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((key): key is string => typeof key === 'string');
  } catch {
    return [];
  }
};

const writeHiddenKeys = (storageKey: string, hiddenKeys: string[]) => {
  try {
    if (hiddenKeys.length) {
      localStorage.setItem(storageKey, JSON.stringify(hiddenKeys));
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch {
    // Storage blocked (private mode): the choice lasts only for this page.
  }
};

export function useColumnVisibility(
  scope: string,
  allColumnKeys: readonly string[],
) {
  const userId = useSelector(sessionSelectors.getUserId);
  const storageKey = buildStorageKey(scope, userId);
  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    const hiddenKeys = readHiddenKeys(storageKey);
    return allColumnKeys.filter((key) => !hiddenKeys.includes(key));
  });

  const toggleColumn = useCallback(
    (key: string) => {
      setVisibleKeys((current) => {
        const next = current.includes(key)
          ? current.filter((visibleKey) => visibleKey !== key)
          : allColumnKeys.filter(
              (columnKey) => columnKey === key || current.includes(columnKey),
            );
        writeHiddenKeys(
          storageKey,
          allColumnKeys.filter((columnKey) => !next.includes(columnKey)),
        );
        return next;
      });
    },
    [allColumnKeys, storageKey],
  );

  const resetColumns = useCallback(() => {
    writeHiddenKeys(storageKey, []);
    setVisibleKeys([...allColumnKeys]);
  }, [allColumnKeys, storageKey]);

  return { visibleKeys, toggleColumn, resetColumns };
}
